import { AlertDialog as BaseAlertDialog } from "@base-ui/react/alert-dialog";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../cn";
import { isSecondPressOfOpening } from "./dialog-dismiss";

// Modal dialogs (design-system.md §3): `Dialog` for a short form that interrupts the work on purpose (a note, a new
// record), `AlertDialog` only for irreversible actions. Focus stays inside, Esc closes, the title names it.

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** The buttons, at the bottom: the primary action last. A form puts its own inside the form. */
  footer?: ReactNode;
}

/** The parts of Base UI's Dialog and AlertDialog: the same shape, a different role for assistive technologies. */
type Parts = typeof BaseDialog | typeof BaseAlertDialog;

function ModalFrame({
  parts: P,
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: DialogProps & { parts: Parts }) {
  return (
    <P.Root
      open={open}
      onOpenChange={(next, details) => {
        if (!next && isSecondPressOfOpening(details)) return details.cancel();
        onOpenChange(next);
      }}
    >
      <P.Portal>
        <P.Backdrop className="fixed inset-0 z-40 bg-black/20 dark:bg-black/50" />
        {/*
          below `sm` almost the whole screen, then centred; the body scrolls, title and footer stay (ADR-050). In a
          window less than 30rem tall (a phone on its side, 400% zoom: 256 px) the title and its description took the
          room of the fields, so there the whole dialog scrolls, with the footer still kept in view
        */}
        <P.Popup className="fixed inset-x-2 top-2 z-40 flex max-h-[calc(100dvh-1rem)] flex-col gap-4 rounded-xl border border-border bg-popover p-5 text-popover-foreground shadow-dialog [@media(max-height:30rem)]:overflow-y-auto sm:top-[12vh] sm:right-auto sm:left-1/2 sm:max-h-[76dvh] sm:w-[32rem] sm:max-w-[calc(100vw-2rem)] sm:-translate-x-1/2">
          <div className="space-y-1">
            <P.Title className="text-base font-semibold">{title}</P.Title>
            {description && <P.Description className="text-sm text-muted-foreground">{description}</P.Description>}
          </div>
          {/* the padding keeps the focus ring of the fields inside the scrolling area */}
          {children && (
            <div className="-m-1 min-h-0 overflow-y-auto p-1 [@media(max-height:30rem)]:shrink-0 [@media(max-height:30rem)]:overflow-visible">
              {children}
            </div>
          )}
          {footer && <DialogFooter>{footer}</DialogFooter>}
        </P.Popup>
      </P.Portal>
    </P.Root>
  );
}

/**
 * The buttons at the bottom of a dialog, the primary last; they wrap when the dialog is narrow. Inside a form that
 * scrolls they stay at the bottom, in view, down to the edge of the scrolling area: its padding (the room for the
 * focus rings) would otherwise show a strip of the fields passing under the buttons.
 */
export function DialogFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("sticky -bottom-1 flex flex-wrap justify-end gap-2 bg-popover pt-2 pb-1", className)}
      {...props}
    />
  );
}

export function Dialog(props: DialogProps) {
  return <ModalFrame parts={BaseDialog} {...props} />;
}

export function AlertDialog(props: DialogProps) {
  return <ModalFrame parts={BaseAlertDialog} {...props} />;
}
